import { BundleIssue, BundleReport } from '../api/bundle';

// 预检结果的"过期"处理 (2026-09-20).
//
// 向导的 dryRun 是**按步**跑的 (点「下一步」才跑一次), 报告会一直挂在界面上直到下次预检。
// 于是出现: 一进来没填就报 "deviceKey 必填", 填好了红字还在 —— 人已经改对了, 界面还在骂他。
//
// 这里按 issue 的 path 把报告"锚"回载荷: 预检时记一份载荷快照, 之后某条 issue 指向的那个值
// 一旦被改动, 这条 issue 就不再可信, 界面上先撤掉 (字段红字、右栏计数、步骤红点一起撤),
// 等下一次预检给出新结论。没被碰过的 issue 原样保留 —— 改 A 字段不该让 B 字段的错误消失。
//
// 只撤显示, 不改 report.ok: 能不能提交仍以服务端最后一次判定为准 (提交时后端还会再校验一遍)。

/** valueAtPath 按 issue 的 path 取载荷里的值; 支持 a.b / a[0].b. 取不到返回 undefined. */
export function valueAtPath(obj: unknown, path: string): unknown {
    if (!path) return undefined;
    let cur: unknown = obj;
    for (const seg of path.replace(/\[(\d+)\]/g, '.$1').split('.')) {
        if (cur === null || cur === undefined || typeof cur !== 'object') return undefined;
        cur = (cur as Record<string, unknown>)[seg];
    }
    return cur;
}

/** 同一条 path 在"预检时的载荷"与"当前载荷"上取值是否一致 (对象/数组按 JSON 比). */
function unchanged(path: string, current: unknown, checked: unknown): boolean {
    const a = valueAtPath(current, path);
    const b = valueAtPath(checked, path);
    if (a === b) return true;
    return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

/**
 * freshIssues 过滤掉"指向的值已被改动"的 issue.
 * checked 为空 (还没预检过) 时原样返回 —— 此时 issues 本来也是空的.
 * path 在载荷里定位不到 (如结构层错误挂在 "bundles") 时保留: 宁可多显示, 不漏报.
 */
export function freshIssues<T>(issues: BundleIssue[], current: T, checked: T | null): BundleIssue[] {
    if (!checked) return issues;
    return issues.filter((i) => {
        if (valueAtPath(checked, i.path) === undefined && valueAtPath(current, i.path) === undefined) {
            return true;
        }
        return unchanged(i.path, current, checked);
    });
}

/** freshReport 报告的三档各自过滤; ok/summary 保持服务端原判 (提交门禁不受显示过滤影响). */
export function freshReport<T>(
    report: BundleReport | null,
    current: T,
    checked: T | null,
): BundleReport | null {
    if (!report || !checked) return report;
    return {
        ...report,
        errors: freshIssues(report.errors, current, checked),
        warnings: freshIssues(report.warnings, current, checked),
        infos: freshIssues(report.infos, current, checked),
    };
}
